import { useState, useEffect } from "react";
import {useNavigate} from "react-router-dom";
import { API_BASE_URL } from "../../config";
import axios from "../../api/axiosInstance";
import '../../css/menumodal.css';

interface MenuItem {
    id: number;
    name: string;
    description: string;
    ingredients: string;
    price: number;
    image_url: string | null;
}

interface PaginationInfo {
    current_page: number;
    per_page: number;
    total_pages: number;
    total_items: number;
}

interface MenuResponse {
    menu: MenuItem[];
    pagination: PaginationInfo;
}

export default function MenuList() {
    const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1, per_page: 5 });
    const [showConfirm, setShowConfirm] = useState(false);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const [selectedImage, setSelectedImage] = useState<File | null>(null);
    const navigate = useNavigate();
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [formData, setFormData] = useState<MenuItem>({
        id: 0,
        name: "",
        description: "",
        ingredients: "",
        price: 0,
        image_url: null,
    });


    useEffect(() => {
        const token = localStorage.getItem("access_token");

        if (!token) {
            console.warn("No token found, redirecting to login...");
            navigate("/login");
            return;
        }

        try {
            const decodedToken = JSON.parse(atob(token.split(".")[1]));
            // console.log("🛠 Decoded Token:", decodedToken);

            const tokenExpiry = decodedToken.exp * 1000;
            if (Date.now() > tokenExpiry) {
                // console.warn("❌ Token expired, logging out...");
                handleLogout();
                return;
            }
        } catch (error) {
            // console.error("❌ Invalid token format:", error);
            handleLogout();
            return;
        }
        fetchMenuItems();
    }, []);
    const fetchMenuItems = async (page = 1) => {
        try {
            const res = await axios.get<MenuResponse>("/menu/get-menu", {
                params: { page, per_page: 6 }
            });

            setMenuItems(res.data.menu);
            setPagination(res.data.pagination);
        } catch (err) {
            console.error("Failed to fetch menu:", err);
        }
    };
    const openModal = (item: MenuItem | null = null) => {
        setFormData(
            item
                ? { ...item }
                : { id: 0, name: "", description: "", ingredients: "", price: 0, image_url: null }
        );
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedImage(null);
        setFormData({ id: 0, name: "", description: "", ingredients: "", price: 0, image_url: null });
    };
    const submitForm = async (event: React.FormEvent) => {
        event.preventDefault();

        setErrors({});

        const newErrors: { [key: string]: string } = {};

        if (!formData.name.trim()) {
            newErrors.name = "Food name is required.";
        }
        if (!formData.description.trim()) {
            newErrors.description = "Description is required.";
        }
        if (!formData.ingredients.trim()) {
            newErrors.ingredients = "Ingredients are required.";
        }
        if (!formData.price || formData.price <= 0) {
            newErrors.price = "Price must be greater than 0.";
        }
        if (!formData.id && !selectedImage && !formData.image_url) {
            newErrors.image = "Please upload an image.";
        }
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        const token = localStorage.getItem("access_token");
        if (!token) {
            console.error("No access token found, redirecting to login...");
            navigate("/login");
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append("name", formData.name);
        formDataToSend.append("description", formData.description);
        formDataToSend.append("ingredients", formData.ingredients);
        formDataToSend.append("price", formData.price.toString());

        if (selectedImage) {
            formDataToSend.append("image", selectedImage);
        } else if (formData.image_url) {
            formDataToSend.append("image_url", formData.image_url);
        }
        const url = formData.id
            ? `${API_BASE_URL}/menu/edit-menu/${formData.id}`
            : `${API_BASE_URL}/menu/add-menu`;

        try {
            await axios.post(url, formDataToSend);
            closeModal();
            fetchMenuItems();
            setCurrentPage(1);
        } catch (error) {
            console.error("Error submitting form:", error);
        }
    };

    const deleteItem = async () => {
        if (deleteId !== null) {
            try {
                await axios.delete(`/menu/delete-menu/${deleteId}`);
                fetchMenuItems();
                setShowConfirm(false);
                setCurrentPage(1);
            } catch (error) {
                console.error("❌ Error deleting item:", error);
            }
        }
    };

    const openConfirmModal = (id: number) => {
        setDeleteId(id);
        setShowConfirm(true);
    };
    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        const newErrors = { ...errors };

        if (file) {
            const validTypes = ["image/jpeg", "image/png", "image/webp"];
            const maxSize = 5 * 1024 * 1024;

            if (!validTypes.includes(file.type)) {
                newErrors.image = "Only JPG, PNG, or WEBP images are allowed.";
                setSelectedImage(null);
            } else if (file.size > maxSize) {
                newErrors.image = "Image must be smaller than 5MB.";
                setSelectedImage(null);
            } else {
                delete newErrors.image;
                setSelectedImage(file);
                setFormData(prev => ({ ...prev, image_url: "" }));
            }
        }

        setErrors(newErrors);
    };

    const handleLogout = async () => {
        try {
            await axios.post("/api/logout");
            sessionStorage.removeItem("restaurant_id");
            sessionStorage.removeItem("restaurant_name");
            navigate("/login");
        } catch (err) {
            console.error("Logout error:", err);
        }
    };

    return (
        <div className="min-h-screen flex flex-col animate-fade-in">

            <div className="flex flex-1">
                <main className="flex-1 p-6 bg-gradient-to-br from-gray-50 to-gray-100 min-h-screen">
                    <div className="max-w-6xl mx-auto">
                        <div className="mb-6 text-center">
                            <h2 className="text-4xl font-extrabold text-gray-800 mb-2">📋 Your Menu</h2>
                            <p className="text-gray-600">Manage your delicious offerings with ease</p>
                        </div>
                        {menuItems.length === 0 && (
                            <div className="text-center text-gray-500 py-10">
                                <p className="text-xl">No items yet...</p>
                                <p className="text-sm">Click the ➕ button to add your first delicious dish!</p>
                            </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                            {menuItems.map((item) => (
                                <div key={item.id} className="bg-white rounded-lg shadow hover:shadow-lg transition p-4 flex flex-col justify-between">
                                    <div>
                                        <img
                                            loading="lazy"
                                            src={item.image_url || "/default-logo.png"}
                                            alt={item.name}
                                            className="w-full h-40 object-cover rounded-md mb-3 transition-transform duration-200 hover:scale-105"
                                        />
                                        <h3 className="text-lg font-semibold text-gray-800">{item.name}</h3>
                                        <p className="text-sm text-gray-600">{item.description}</p>
                                        <p className="text-xs text-gray-500 mt-1">Ingredients: {item.ingredients}</p>
                                        <p className="mt-2 font-bold text-blue-600">${item.price.toFixed(2)}</p>
                                    </div>
                                    <div className="flex justify-end gap-2 mt-4">
                                        <button onClick={() => openModal(item)} title="Edit Menu" className="text-yellow-600 hover:text-yellow-700">✏️</button>
                                        <button onClick={() => openConfirmModal(item.id)} title="Delete Menu" className="text-red-500 hover:text-red-600">🗑️</button>
                                    </div>
                                </div>
                            ))}

                        </div>
                        <div className="flex justify-center mt-4 gap-2 flex-wrap">
                            {Array.from({length: pagination.total_pages}, (_, i) => (
                                <button
                                    key={i}
                                    onClick={() => fetchMenuItems(i + 1)}
                                    className={`px-3 py-1 border rounded transition ${
                                        i + 1 === pagination.current_page
                                            ? 'bg-blue-600 text-white font-semibold'
                                            : 'bg-white text-gray-800 hover:bg-gray-100'
                                    }`}
                                >
                                    {i + 1}
                                </button>
                            ))}
                        </div>
                    </div>
                </main>
            </div>
            {/* Confirmation Modal */}
            {showConfirm && (
                <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-lg max-w-sm text-center">
                        <h4 className="text-lg font-bold mb-4">Are you sure?</h4>
                        <p className="text-gray-600">Do you really want to delete this item?</p>

                        <div className="flex justify-between mt-4">
                            <button
                                className="bg-gray-400 hover:bg-gray-500 text-white py-2 px-4 rounded"
                                onClick={() => setShowConfirm(false)}
                            >
                                Cancel
                            </button>
                            <button
                                className="bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded"
                                onClick={deleteItem}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <button
                onClick={() => openModal()}
                className="fixed bottom-6 right-6 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white py-3 px-4 rounded-full shadow-xl text-2xl transition"
            >
                ➕
            </button>
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center transition-opacity duration-300">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-lg w-full animate-fade-in">
                        <h4 className="text-lg font-bold mb-4">Add/Edit Menu Item</h4>
                        <form onSubmit={submitForm} encType="multipart/form-data">
                            <input type="hidden" value={formData.id} />

                            <div className="mb-3">
                                <label className="block text-gray-700">Food Name:</label>
                                <input
                                    type="text"
                                    className={`w-full p-2 border rounded ${errors.name ? "border-red-500" : ""}`}
                                    value={formData.name}
                                    onChange={(e) => {
                                        setFormData({ ...formData, name: e.target.value });
                                        setErrors({ ...errors, name: "" });
                                    }}
                                    required
                                />
                                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                            </div>
                            <div className="mb-3">
                                <label className="block text-gray-700">Description:</label>
                                <textarea
                                    className={`w-full p-2 border rounded ${errors.description ? "border-red-500" : ""}`}
                                    value={formData.description}
                                    onChange={(e) => {
                                        setFormData({ ...formData, description: e.target.value });
                                        setErrors({ ...errors, description: "" });
                                    }}
                                    required
                                />
                                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                            </div>

                            <div className="mb-3">
                                <label className="block text-gray-700">Ingredients:</label>
                                <textarea
                                    className={`w-full p-2 border rounded ${errors.ingredients ? "border-red-500" : ""}`}
                                    value={formData.ingredients}
                                    onChange={(e) => {
                                        setFormData({ ...formData, ingredients: e.target.value });
                                        setErrors({ ...errors, ingredients: "" });
                                    }}
                                    required
                                />
                                {errors.ingredients && <p className="text-red-500 text-sm mt-1">{errors.ingredients}</p>}
                            </div>

                            <div className="mb-3">
                                <label className="block text-gray-700">Price ($):</label>
                                <input
                                    type="number"
                                    className={`w-full p-2 border rounded ${errors.price ? "border-red-500" : ""}`}
                                    value={formData.price}
                                    onChange={(e) => {
                                        setFormData({ ...formData, price: parseFloat(e.target.value) || 0 });
                                        setErrors({ ...errors, price: "" });
                                    }}
                                    step="0.01"
                                    required
                                />
                                {errors.price && <p className="text-red-500 text-sm mt-1">{errors.price}</p>}
                            </div>
                            <div className="mb-3">
                                <label className="block text-gray-700">Upload Image:</label>
                                <input
                                    type="file"
                                    className={`w-full p-2 border rounded ${errors.image ? "border-red-500" : ""}`}
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                                {errors.image && <p className="text-red-500 text-sm mt-1">{errors.image}</p>}
                            </div>

                            {(selectedImage || (formData.image_url && !selectedImage)) && (
                                <div className="mb-3 flex justify-center">
                                    <img
                                        src={
                                            selectedImage
                                                ? URL.createObjectURL(selectedImage)
                                                : formData.image_url?.startsWith("http")
                                                    ? formData.image_url
                                                    : `${API_BASE_URL}/static/restaurants/${localStorage.getItem("restaurant_slug")}/menus/${formData.image_url}`
                                        }
                                        alt="Preview"
                                        className="w-32 h-32 object-cover rounded border"
                                        onError={(e) => (e.currentTarget.style.display = "none")}
                                    />
                                </div>
                            )}

                            <div className="flex justify-between">
                                <button
                                    type="button"
                                    className="bg-gray-400 hover:bg-gray-500 text-white py-2 px-4 rounded"
                                    onClick={closeModal}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded"
                                >
                                    Save Item
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

        </div>
    );
}
